/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { HeroModule, isInvertedColor, toLayoutValue } from '@coursera-web/cds-labs';

import { Button, Hidden, breakpoints } from '@coursera/cds-core';
import type { ButtonName, MerchandisingModuleModuleType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { useCampaignModulesData } from 'bundles/campaigns/context/CampaignModulesDataContext';

type HeroModuleProps = React.ComponentProps<typeof HeroModule>;

const styles = {
  container: css`
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,
  heading: css`
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,
  description: css`
    margin-bottom: var(--cds-spacing-200);
    white-space: pre-line;

    ${breakpoints.up('md')} {
      width: 80%;
    }
  `,
  button: css`
    && {
      text-decoration: none;
    }

    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,
  secondaryButton: css`
    && {
      background-color: transparent;
    }
  `,
  buttonWithBorder: css`
    &&,
    &:hover {
      border: 1px solid var(--cds-color-interactive-stroke-primary-focus-invert);
    }
  `,
};

const HeroModuleRenderer = () => {
  const track = useTracker();

  const moduleData = useCampaignModulesData()?.campaignData?.heroModule;

  const content = moduleData?.contentDataJson;

  const {
    description,
    backgroundColor,
    ctaText,
    ctaTarget,
    headline,
    secondaryCtaTarget,
    secondaryCtaText,
    layout,
    eyebrow,
    eyebrowBackground,
    eyebrowImage,
    image,
    imageMobile,
    imageTablet,
  } = content || {};

  const moduleEventingData = {
    moduleType: 'hero_module' as MerchandisingModuleModuleType,
    heading: headline ?? '',
    body: description ?? '',
    hasImage: !!image?.url,
    campaign: {
      id: moduleData?.campaignId ?? '',
      matchedCountryCode: moduleData?.matchedCountryCode ?? '',
      epicExperimentId: moduleData?.epicExperimentId ?? '',
      regionName: moduleData?.regionName ?? '',
      name: moduleData?.campaignName ?? '',
    },
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_merchandising_module',
    {
      merchandisingModule: moduleEventingData,
    }
  );

  const trackClick = (buttonName: ButtonName, linkName: string, linkTarget: string) => () => {
    track('click_merchandising_module', {
      merchandisingModule: moduleEventingData,
      button: {
        name: buttonName,
        linkURL: linkTarget,
        linkName,
        linkType: 'internal',
      },
    });
  };

  const descriptionToShow = (descriptionCopy?: string | null) => (
    <p css={styles.description}>{descriptionCopy ?? ''}</p>
  );

  const titleToShow = (titleCopy: string) => <div css={styles.heading}>{titleCopy}</div>;

  const isDarkBackground = isInvertedColor(backgroundColor ?? '');

  const getHeroModuleProps = ({ isMobile, isTablet }: { isMobile?: boolean; isTablet?: boolean }): HeroModuleProps => {
    let heroImage = image;
    if (isMobile && imageMobile) {
      heroImage = imageMobile;
    } else if (isTablet && imageTablet) {
      heroImage = imageTablet;
    }

    const eyebrowImageHeight = eyebrowImage?.height ? Math.min(eyebrowImage.height, 16) : undefined;

    return {
      ...content,
      image: {
        ...heroImage,
      },
      eyebrow: {
        label: eyebrow,
        image: {
          ...eyebrowImage,
          ...(eyebrowImageHeight ? { height: isMobile ? eyebrowImageHeight * 0.75 : eyebrowImageHeight } : {}),
        },
        backgroundColor: eyebrowBackground,
      },
      headline: titleToShow(headline ?? ''),
      description: descriptionToShow(description),
      layout: toLayoutValue(layout),
      preloadImage: true,
      customPrimaryCta:
        ctaText && ctaTarget ? (
          <Button
            variant={isDarkBackground ? 'secondary' : 'primary'}
            component="a"
            href={ctaTarget}
            css={styles.button}
            onClick={trackClick('primary_cta', ctaText, ctaTarget)}
          >
            {ctaText}
          </Button>
        ) : undefined,
      customSecondaryCta:
        secondaryCtaText && secondaryCtaTarget ? (
          <Button
            variant={isDarkBackground ? 'primary' : 'secondary'}
            component="a"
            href={secondaryCtaTarget}
            css={[styles.button, styles.secondaryButton, isDarkBackground ? styles.buttonWithBorder : undefined]}
            onClick={trackClick('secondary_cta', secondaryCtaText, secondaryCtaTarget)}
          >
            {secondaryCtaText}
          </Button>
        ) : undefined,
      textAlignmentOnMobile: 'Center',
    };
  };

  return (
    <div css={styles.container} ref={visibilityTrackingRef}>
      <Hidden smUp>
        <HeroModule {...getHeroModuleProps({ isMobile: true, isTablet: false })} />
      </Hidden>
      <Hidden mdUp xsDown>
        <HeroModule {...getHeroModuleProps({ isMobile: false, isTablet: true })} />
      </Hidden>
      <Hidden smDown>
        <HeroModule {...getHeroModuleProps({ isMobile: false, isTablet: false })} />
      </Hidden>
    </div>
  );
};

export default HeroModuleRenderer;
